import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUser } from "@/hooks/useCurrentUser";

// Import preset backgrounds - using WebP for better compression
import mobileGradientBg from "@/assets/backgrounds/mobile-gradient.webp";

export type BackgroundPreset = 
  | "gradient-pink" 
  | "gradient-ocean" 
  | "gradient-sunset" 
  | "gradient-mint" 
  | "gradient-dark"
  | "custom";

interface BackgroundContextType {
  backgroundPreset: BackgroundPreset;
  customBackgroundUrl: string | null;
  currentBackgroundUrl: string;
  setBackgroundPreset: (preset: BackgroundPreset) => Promise<void>;
  setCustomBackground: (url: string) => Promise<void>;
  isLoading: boolean;
}

const BackgroundContext = createContext<BackgroundContextType | undefined>(undefined);

// Preset background URLs - these would be stored in public/backgrounds
const PRESET_BACKGROUNDS: Record<Exclude<BackgroundPreset, "custom">, string> = {
  "gradient-pink": mobileGradientBg, // Current default
  "gradient-ocean": "/backgrounds/ocean-gradient.png",
  "gradient-sunset": "/backgrounds/sunset-gradient.png", 
  "gradient-mint": "/backgrounds/mint-gradient.png",
  "gradient-dark": "/backgrounds/dark-gradient.png",
};

interface BackgroundProviderProps {
  children: ReactNode;
}

export function BackgroundProvider({ children }: BackgroundProviderProps) {
  const { data: user } = useCurrentUser();
  const [backgroundPreset, setBackgroundPresetState] = useState<BackgroundPreset>("gradient-pink");
  const [customBackgroundUrl, setCustomBackgroundUrlState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user's background preference
  useEffect(() => {
    const loadBackgroundPreference = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("user_preferences")
          .select("*")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error) {
          console.error("[BackgroundContext] Error loading preference:", error);
        } else if (data) {
          // Cast to handle dynamic columns
          const prefs = data as Record<string, unknown>;
          setBackgroundPresetState((prefs.background_type as BackgroundPreset) || "gradient-pink");
          setCustomBackgroundUrlState((prefs.custom_background_url as string) || null);
        }
      } catch (error) {
        console.error("[BackgroundContext] Failed to load preference:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadBackgroundPreference();
  }, [user?.id]);

  const setBackgroundPreset = async (preset: BackgroundPreset) => {
    setBackgroundPresetState(preset);
    
    if (!user?.id) return;

    try {
      await supabase
        .from("user_preferences")
        .upsert({
          user_id: user.id,
          background_type: preset,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: "user_id"
        });
    } catch (error) {
      console.error("[BackgroundContext] Failed to save preset:", error);
    }
  };

  const setCustomBackground = async (url: string) => {
    setCustomBackgroundUrlState(url);
    setBackgroundPresetState("custom");
    
    if (!user?.id) return;

    try {
      await supabase
        .from("user_preferences")
        .upsert({
          user_id: user.id,
          background_type: "custom",
          custom_background_url: url,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: "user_id"
        });
    } catch (error) {
      console.error("[BackgroundContext] Failed to save custom background:", error);
    }
  };

  // Compute current background URL
  const currentBackgroundUrl = 
    backgroundPreset === "custom" && customBackgroundUrl
      ? customBackgroundUrl
      : PRESET_BACKGROUNDS[backgroundPreset as Exclude<BackgroundPreset, "custom">] || mobileGradientBg;

  return (
    <BackgroundContext.Provider
      value={{
        backgroundPreset,
        customBackgroundUrl,
        currentBackgroundUrl,
        setBackgroundPreset,
        setCustomBackground,
        isLoading,
      }}
    >
      {children}
    </BackgroundContext.Provider>
  );
}

export function useBackground() {
  const context = useContext(BackgroundContext);
  if (context === undefined) {
    throw new Error("useBackground must be used within a BackgroundProvider");
  }
  return context;
}
